/**
 * Rewrite a hardcoded production peer URL (`https://<slug>.ordo.com/...`)
 * onto the deployment the current page is actually served from.
 *
 * Every Ordo app hardcodes its peers' PRODUCTION origins as the fallback
 * for a build-time env var (`https://hq.ordo.com` for the app switcher,
 * `https://agent.ordo.com` for the agent embed, `https://schedule.ordo.com`
 * for the events widget, …). That fallback is correct in production and
 * harmless in local dev — but in a PREVIEW ENV it breaks encapsulation:
 * a page served from `tasks.<envId>.preview.ordo.com` fetches PRODUCTION
 * hq/agent/road-warrior, which is both a data leak and pure 401 noise
 * (the preview session cookie is scoped to `.<envId>.preview.ordo.com`).
 * Client bundles are also built inside the env, so `VITE_*` /
 * `NEXT_PUBLIC_*` overrides cannot be injected per-env after the fact.
 *
 * Since preview envs mirror the whole cluster under one parent domain,
 * the peer is always reachable at the SAME slug under the page's own
 * parent domain. So derive it from `location` instead of configuring it:
 *
 *   page                                 `https://hq.ordo.com`
 *   ----------------------------------   -----------------------------------
 *   tasks.ordo.com                    →  https://hq.ordo.com  (unchanged)
 *   tasks.cms1234.preview.ordo.com    →  https://hq.cms1234.preview.ordo.com
 *   localhost:3001                    →  https://hq.ordo.com  (unchanged)
 *   partner.example.com               →  https://hq.ordo.com  (unchanged)
 *
 * Only `*.ordo.com` peers on a page whose own parent domain is under
 * `ordo.com` are rewritten, so localhost dev, cross-domain partner
 * embeds, and third-party URLs all pass through untouched. Path, query,
 * and fragment are preserved verbatim.
 *
 * Callers that DO have a per-env value (an env var, `window.location.origin`
 * for a same-origin peer) should keep using it — this is for the
 * hardcoded-production-fallback case only.
 */

/** Hosts under this domain are treated as "an Ordo deployment ring". */
const ORDO_DOMAIN = 'ordo.com'

/**
 * @param url  A peer URL whose host may be `<slug>.ordo.com`.
 * @param host Page hostname; defaults to `location.hostname`. Absent
 *             (SSR / non-browser) means "leave the URL alone".
 */
export function sameEnvOrigin(url: string, host?: string): string {
  const pageHost = host ?? globalThis.location?.hostname
  if (!pageHost) {
    return url
  }
  // Drop the app's own label: `tasks.cms1234.preview.ordo.com` →
  // `cms1234.preview.ordo.com`; `tasks.ordo.com` → `ordo.com` (identity).
  const parentDomain = pageHost.split('.').slice(1).join('.')
  // Exact match or a true subdomain — a bare `endsWith` would also accept
  // a partner host like `notordo.com` and retarget its embed.
  if (parentDomain !== ORDO_DOMAIN && !parentDomain.endsWith(`.${ORDO_DOMAIN}`)) {
    return url
  }
  return url.replace(/^(https?:\/\/)([a-z0-9-]+)\.ordo\.com(?=[/?#]|$)/i, `$1$2.${parentDomain}`)
}

/**
 * `https://<slug>.<the page's parent domain>` — or `null` when the page
 * isn't served from a non-production Ordo ring, so the caller keeps its
 * own production constant.
 *
 * For the peers whose PRODUCTION host label differs from their preview
 * slug, which `sameEnvOrigin` therefore cannot derive: the Ordo app is
 * `app.ordo.com` in production but `ordo.<envId>.…` in a preview env,
 * and Menu Builder is `menubuilder.ordo.com` but `menus.<envId>.…`.
 * Pass the PREVIEW slug (see each app's `.preview.json`).
 */
export function sameEnvSlugOrigin(slug: string, host?: string): string | null {
  const pageHost = host ?? globalThis.location?.hostname
  if (!pageHost) {
    return null
  }
  const parentDomain = pageHost.split('.').slice(1).join('.')
  // `ordo.com` exactly = production; anything else under it = a preview ring.
  if (parentDomain === ORDO_DOMAIN || !parentDomain.endsWith(`.${ORDO_DOMAIN}`)) {
    return null
  }
  return `https://${slug}.${parentDomain}`
}
